﻿import React, { useContext, useCallback, useState } from 'react';
import Context, { context_type } from '@Util/Context';
import { useTranslation } from 'react-i18next';
import { str_isEmpty } from '@Util/Utils';
import {
    Icon_Play,
    Icon_Stop,
    Icon_Plus,
    Icon_Spin
} from '@Elements/ImageElements/Icons';

import {
    Start_Button_Props,
    Stop_Button_Props,
    Plus_Button_Props,
    Pomodoro_Button_Props,
    Button_Props,
    PDF_Button_Props,
    Excel_Button_Props
} from '@Util/Types';

/*export function Start_Button(props: Start_Button_Props): JSX.Element {
    const [t] = useTranslation();
    const { start_time }: context_type = useContext(Context);
    const [is_processing, set_is_processing] = useState<boolean>(false);

    async function start_time_function(): Promise<void> {
        set_is_processing(true);

        await start_time(props.start_time_id);

        set_is_processing(false);
    }

    return (
        <button
            type="button"
            className="Button Button_Start"
            onClick={() =>
                start_time_function()
            }
            title={`${t('Text.StartTimeOnProject')} ${props.project_name}`}
            aria-name={`${t('Text.StartTimeOnProject')} ${props.project_name}`}
            disabled={is_processing}
        >
            {
                is_processing ?
                    <Icon_Spin /> :
                    <Icon_Play />
            }
        </button>
    )
}*/

export function Start_Button(props: Start_Button_Props): JSX.Element {
    const [t] = useTranslation();
    const { start_time }: context_type = useContext(Context);

    const [is_processing, set_is_processing] = useState<boolean>(false);
    const [show_spinner, set_show_spinner] = useState<boolean>(false);

    async function start_time_function(): Promise<void> {
        let spinnerTimeout: NodeJS.Timeout | null = null;

        // Only show spinner if the function takes longer than 500ms
        spinnerTimeout = setTimeout(() => {
            set_show_spinner(true);
            set_is_processing(true);
        }, 500);

        try {
            await start_time(props.start_time_id);
        } finally {
            if (spinnerTimeout) clearTimeout(spinnerTimeout);

            set_show_spinner(false);
            set_is_processing(false);
        }
    }

    return (
        <button
            type="button"
            className="Button Button_Start"
            onClick={start_time_function}
            title={`${t('Text.StartTimeOnProject')} ${props.project_name}`}
            aria-name={`${t('Text.StartTimeOnProject')} ${props.project_name}`}
            disabled={is_processing}
        >
            {show_spinner ? <Icon_Spin /> : <Icon_Play />}
        </button>
    );
}

/*export const Stop_Button: React.FC<Stop_Button_Props> = React.memo((props: Stop_Button_Props) => {
    const [t] = useTranslation();
    const { stop_time, current_project }: context_type = useContext(Context);

    const handleStopTime = useCallback(() => {
        if (stop_time && current_project?.id) {
            stop_time(current_project.id.toString());
        } else {
            console.error('stop_time function or current_project id is not defined');
        }
    }, [stop_time, current_project]);

    return (
        <button
            type="button"
            className="Button Button_Red"
            onClick={handleStopTime}
            title={`${t('Text.StopTimeOnProject')} ${props.project_name}`}
            aria-label={`${t('Text.StopTimeOnProject')} ${props.project_name}`}
        >
            <Icon_Stop />
        </button>
    );
});*/

export const Stop_Button: React.FC<Stop_Button_Props> = React.memo((props: Stop_Button_Props) => {
    const [t] = useTranslation();
    const { stop_time, current_project }: context_type = useContext(Context);

    const [is_processing, set_is_processing] = useState(false);
    const [show_spinner, set_show_spinner] = useState(false);

    const handleStopTime = useCallback(async () => {
        if (!stop_time || !current_project?.id) {
            console.error('stop_time function or current_project id is not defined');
            return;
        }

        let spinnerTimeout: NodeJS.Timeout | null = null;

        // Only show spinner if the function takes longer than 500ms
        spinnerTimeout = setTimeout(() => {
            set_show_spinner(true);
            set_is_processing(true);
        }, 500);

        try {
            await stop_time(current_project.id.toString());
        } finally {
            if (spinnerTimeout) clearTimeout(spinnerTimeout);

            set_show_spinner(false);
            set_is_processing(false);
        }
    }, [stop_time, current_project]);

    return (
        <button
            type="button"
            className="Button Button_Red"
            onClick={handleStopTime}
            title={`${t('Text.StopTimeOnProject')} ${props.project_name}`}
            aria-label={`${t('Text.StopTimeOnProject')} ${props.project_name}`}
            disabled={is_processing}
        >
            {show_spinner ? <Icon_Spin /> : <Icon_Stop />}
        </button>
    );
});

export const Plus_Button: React.FC<Plus_Button_Props> = React.memo((props: Plus_Button_Props) => {
    const { className, onClick, aria_name } = props;

    const generate_button_classes = useCallback((): string => {
        return `Button Button_Icon ${!str_isEmpty(className) ? className : ''}`.trim();
    }, [className]);

    return (
        <button
            type="button"
            className={generate_button_classes()}
            onClick={onClick}
            title={aria_name}
            aria-label={aria_name}
        >
            <Icon_Plus />
        </button>
    );
});

export function Pomodoro_Button(props: Pomodoro_Button_Props): JSX.Element {

    function generate_button_classes(): string {
        let class_str: string = "Button Button_Icon";

        if (!str_isEmpty(props.className)) {
            class_str += ` ${props.className}`;
        }

        return class_str;
    }

    return (
        <button
            type="button"
            className={generate_button_classes()}
            onClick={props.onClick}
            title={props.aria_name}
            aria-name={props.aria_name}
        >
            { props.children }
        </button>
    )
}

export const Button: React.FC<Button_Props> = React.memo((props: Button_Props) => {
    const { type = "button", color, className, onClick, children } = props;

    const generate_button_classes = useCallback((): string => {
        let class_str = `Button Button_${color}`;
        if (!str_isEmpty(className)) {
            class_str += ` ${className}`;
        }
        return class_str;
    }, [color, className]);

    return (
        <button
            type={type}
            className={generate_button_classes()}
            onClick={onClick}
        >
            {children}
        </button>
    );
});

export function PDF_Button(props: PDF_Button_Props): JSX.Element {
    const [t] = useTranslation();

    return (
        <button
            type="button"
            className="Button_PDF"
            onClick={props.onClick}
            aria-label={t('Text.DownloadAsPDF')}
            disabled={props.loading}
        >
            {t('Text.DownloadAsPDF')}
            {
                props.loading ?
                    <svg xmlns="http://www.w3.org/2000/svg" width="22.6px" height="22.6px" viewBox="0 0 24 24"><path fill="currentColor" d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z"><animateTransform attributeName="transform" dur="0.75s" repeatCount="indefinite" type="rotate" values="0 12 12;360 12 12" /></path></svg> :
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path d="M181.9 256.1c-5-16-4.9-46.9-2-46.9 8.4 0 7.6 36.9 2 46.9zm-1.7 47.2c-7.7 20.2-17.3 43.3-28.4 62.7 18.3-7 39-17.2 62.9-21.9-12.7-9.6-24.9-23.4-34.5-40.8zM86.1 428.1c0 .8 13.2-5.4 34.9-40.2-6.7 6.3-29.1 24.5-34.9 40.2zM248 160h136v328c0 13.3-10.7 24-24 24H24c-13.3 0-24-10.7-24-24V24C0 10.7 10.7 0 24 0h200v136c0 13.2 10.8 24 24 24zm-8 171.8c-20-12.2-33.3-29-42.7-53.8 4.5-18.5 11.6-46.6 6.2-64.2-4.7-29.4-42.4-26.5-47.8-6.8-5 18.3-.4 44.1 8.1 77-11.6 27.6-28.7 64.6-40.8 85.8-.1 0-.1 .1-.2 .1-27.1 13.9-73.6 44.5-54.5 68 5.6 6.9 16 10 21.5 10 17.9 0 35.7-18 61.1-61.8 25.8-8.5 54.1-19.1 79-23.2 21.7 11.8 47.1 19.5 64 19.5 29.2 0 31.2-32 19.7-43.4-13.9-13.6-54.3-9.7-73.6-7.2zM377 105L279 7c-4.5-4.5-10.6-7-17-7h-6v128h128v-6.1c0-6.3-2.5-12.4-7-16.9zm-74.1 255.3c4.1-2.7-2.5-11.9-42.8-9 37.1 15.8 42.8 9 42.8 9z" /></svg>
            }
        </button>
    )
}

export function Excel_Button(props: Excel_Button_Props): JSX.Element {
    const [t] = useTranslation();

    return (
        <button
            type="button"
            className="Button_Excel"
            onClick={props.onClick}
            aria-label={t('Text.DownloadAsExcel')}
            disabled={props.loading}
        >
            {t('Text.DownloadAsExcel')}
            {
                props.loading ?
                    <svg xmlns="http://www.w3.org/2000/svg" width="22.6px" height="22.6px" viewBox="0 0 24 24"><path fill="currentColor" d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z"><animateTransform attributeName="transform" dur="0.75s" repeatCount="indefinite" type="rotate" values="0 12 12;360 12 12" /></path></svg> :
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path d="M224 136V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24V160H248c-13.2 0-24-10.8-24-24zm60.1 106.5L224 336l60.1 93.5c5.1 8-.6 18.5-10.1 18.5h-34.9c-4.4 0-8.5-2.4-10.6-6.3C208.9 405.5 192 373 192 373c-6.4 14.8-10 20-36.6 68.8-2.1 3.9-6.1 6.3-10.5 6.3H110c-9.5 0-15.2-10.5-10.1-18.5l60.3-93.5-60.3-93.5c-5.2-8 .6-18.5 10.1-18.5h34.8c4.4 0 8.5 2.4 10.6 6.3 26.1 48.8 20 33.6 36.6 68.5 0 0 6.1-11.7 36.6-68.5 2.1-3.9 6.2-6.3 10.6-6.3H274c9.5-.1 15.2 10.4 10.1 18.4zM384 121.9v6.1H256V0h6.1c6.4 0 12.5 2.5 17 7l97.9 98c4.5 4.5 7 10.6 7 16.9z" /></svg>
            }
        </button>
    )
}